// import React from 'react';
// import { motion } from 'motion/react';
// import { useInView } from 'react-intersection-observer';
// import { TrendingUp, Search, Target } from 'lucide-react';

// export function SEOSection() {
//   const [ref, inView] = useInView({
//     triggerOnce: true,
//     threshold: 0.2,
//   });

//   return (
//     <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-20 relative overflow-hidden">
//       <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-primary/10 to-secondary/10 dark:from-accent/20 dark:via-primary/20 dark:to-secondary/20" />

//       <div className="max-w-5xl mx-auto relative">
//         <motion.div
//           ref={ref}
//           initial={{ opacity: 0, y: 40 }}
//           animate={inView ? { opacity: 1, y: 0 } : {}}
//           transition={{ duration: 0.8 }}
//         >
//           <div className="relative bg-gradient-to-br from-card/80 to-muted/80 backdrop-blur-xl border-2 border-transparent rounded-3xl p-8 lg:p-16 shadow-2xl overflow-hidden group hover:border-primary/30 transition-all duration-500">
//             {/* Animated Gradient Border */}
//             <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary via-accent to-secondary opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-xl" />

//             {/* Glow Effect */}
//             <div className="absolute -top-32 -right-32 w-64 h-64 bg-primary/30 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
//             <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-accent/30 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

//             <div className="relative space-y-8">
//               {/* Icons */}
//               <motion.div
//                 initial={{ opacity: 0, scale: 0.8 }}
//                 animate={inView ? { opacity: 1, scale: 1 } : {}}
//                 transition={{ delay: 0.3 }}
//                 className="flex justify-center gap-4"
//               >
//                 <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent p-4 shadow-lg">
//                   <Search className="w-full h-full text-white" />
//                 </div>
//                 <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-secondary p-4 shadow-lg">
//                   <TrendingUp className="w-full h-full text-white" />
//                 </div>
//                 <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-secondary to-primary p-4 shadow-lg">
//                   <Target className="w-full h-full text-white" />
//                 </div>
//               </motion.div>

//               {/* Title */}
//               <motion.h2
//                 initial={{ opacity: 0, y: 20 }}
//                 animate={inView ? { opacity: 1, y: 0 } : {}}
//                 transition={{ delay: 0.4 }}
//                 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center"
//               >
//                 Stand Out with{' '}
//                 <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
//                   SEO-Optimized
//                 </span>
//                 <br />
//                 NFC Card Designs
//               </motion.h2>

//               {/* Body Text */}
//               <motion.p
//                 initial={{ opacity: 0, y: 20 }}
//                 animate={inView ? { opacity: 1, y: 0 } : {}}
//                 transition={{ delay: 0.5 }}
//                 className="text-lg lg:text-xl text-muted-foreground text-center max-w-3xl mx-auto leading-relaxed"
//               >
//                 Our NFC card designs don't just look great — they're SEO-optimized to drive traffic and boost online visibility. 
//                 Make a lasting impression and attract more customers with NFC cards built to perform.
//               </motion.p>

//               {/* CTA */}
//               <motion.div
//                 initial={{ opacity: 0, y: 20 }}
//                 animate={inView ? { opacity: 1, y: 0 } : {}}
//                 transition={{ delay: 0.6 }}
//                 className="flex justify-center"
//               >
//                 <motion.button
//                   className="px-8 py-4 bg-gradient-to-r from-primary via-accent to-secondary text-white rounded-xl font-semibold shadow-lg hover:shadow-2xl hover:shadow-primary/50 dark:hover:shadow-primary/30 transition-all duration-300"
//                   whileHover={{ scale: 1.05, y: -2 }}
//                   whileTap={{ scale: 0.95 }}
//                 >
//                   Explore Possibilities
//                 </motion.button>
//               </motion.div>
//             </div>
//           </div>
//         </motion.div>
//       </div>
//     </section>
//   );
// }




import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Search, Target } from "lucide-react";

/* ===== COLOR SYSTEM (ONE PLACE) ===== */
const COLORS = {
  primary: "#6366f1",   // Indigo
  accent: "#22d3ee",    // Cyan
  textDark: "#111",
  textMuted: "#6b7280",
};

const SEOSection = () => {
  const [inView, setInView] = useState(false);

  /* ===== SCROLL ANIMATION ===== */
  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById("seo-section");
      if (!section) return;

      const rect = section.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.8) {
        setInView(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section
      id="seo-section"
      style={{
        padding: "80px 16px",
        background: `linear-gradient(135deg,
          rgba(99,102,241,0.1),
          rgba(34,211,238,0.1)
        )`,
      }}
    >
      <div style={{ maxWidth: "960px", margin: "0 auto" }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          {/* ===== CARD ===== */}
          <div
            style={{
              padding: "48px 32px",
              borderRadius: "32px",
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(12px)",
              border: `2px solid rgba(99,102,241,0.15)`,
              boxShadow: "0 30px 60px rgba(0,0,0,0.15)",
            }}
          >
            {/* ===== ICONS ===== */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.3 }}
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                marginBottom: "32px",
              }}
            >
              {[Search, TrendingUp, Target].map((Icon, index) => (
                <div
                  key={index}
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "16px",
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.accent})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 20px rgba(0,0,0,0.25)",
                  }}
                >
                  <Icon size={32} color="#fff" />
                </div>
              ))}
            </motion.div>

            {/* ===== TITLE ===== */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
              style={{
                textAlign: "center",
                fontSize: "2.5rem",
                fontWeight: "bold",
                color: COLORS.textDark,
                marginBottom: "24px",
              }}
            >
              Stand Out with{" "}
              <span
                style={{
                  background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.accent})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                SEO-Optimized
              </span>
              <br />
              NFC Card Designs
            </motion.h2>

            {/* ===== DESCRIPTION ===== */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
              style={{
                textAlign: "center",
                fontSize: "1.125rem",
                color: COLORS.textMuted,
                maxWidth: "720px",
                margin: "0 auto 40px",
                lineHeight: 1.7,
              }}
            >
              Our NFC card designs don’t just look great — they’re SEO-optimized
              to drive traffic and boost online visibility. Make a lasting
              impression and attract more customers with NFC cards built to
              perform.
            </motion.p>

            {/* ===== BUTTON ===== */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  padding: "16px 32px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "#fff",
                  background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.accent})`,
                  borderRadius: "16px",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 12px 30px rgba(99,102,241,0.4)",
                }}
              >
                Explore Possibilities
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SEOSection;

